import {
  Add,
  Check,
  DeleteOutlined,
  Error,
  ExpandMore,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { ControlledSelectField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-select-field";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { FormFieldWrapper } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field-wrapper";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { useState } from "react";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  ITEM_NAMES,
  Profile,
  Project,
  PROJECT_NAMES,
} from "vmd-types";

import {
  PURPLE_BADGE_BACKGROUND,
  SUCCESS_COLORS,
} from "../../constants/colors";
import { theme } from "../../theme";
import {
  generateAccessString,
  getAccessOptions,
  handleAccessChange,
  handleProjectIdsChange,
} from "./manage-users.helpers";

type ManageUserRow = Profile & {
  rowKey?: string;
};

type ManageUsersPageProps = {
  configuration?: Configuration;
  language?: string;
};

export const ManageUsersPage: PimoReactComponent<ManageUsersPageProps> = ({
  language = "en",
}) => {
  const { setValue, formState } = usePimoForm<{
    basicItems: BasicItem[];
    configuration?: Configuration;
    projects: Project[];
    validationErrors?: Record<string, ExtendedFieldError>;
    clientUsers?: Profile[];
    toolUsers?: ManageUserRow[];
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const [openDelete, setOpenDelete] = useState(false);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<
    string[] | undefined
  >(undefined);

  const handleAppend = () => {
    const newItem: ManageUserRow = {
      id: crypto.randomUUID(),
      rowKey: crypto.randomUUID(),
      groups: [""],
      email: "",
      name: "",
      permissions: {
        role: undefined,
        client: formState.configuration?.client ?? "",
        tool: formState.configuration?.tool ?? "",
        product: "pimo-product",
      },
      flag: "add",
    };

    setValue("toolUsers", [...(formState?.toolUsers ?? []), newItem]);
    if (formState.enableSaveButton !== true) {
      setValue("enableSaveButton", true);
    }

    setValue("hasUpdatedUsers", true);
    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (indices: string[]) => {
    setCurrentSelectedItem(indices);
    setOpenDelete(true);
  };
  const handleConfirmDelete = () => {
    if (currentSelectedItem === undefined) {
      return;
    }

    const updatedUsers = formState?.toolUsers?.map((user) => {
      if (currentSelectedItem.includes(user.id ?? "")) {
        return { ...user, flag: "delete" };
      }
      return user;
    });

    setValue("toolUsers", updatedUsers);
    if (formState.enableSaveButton !== true) {
      setValue("enableSaveButton", true);
    }
    setOpenDelete(false);
    setCurrentSelectedItem(undefined);

    setValue("hasUpdatedUsers", true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setCurrentSelectedItem(undefined);
  };

  const activeUserIds =
    formState.toolUsers?.filter((u) => u.flag !== "delete").map((u) => u.id) ??
    [];

  const itemName =
    ITEM_NAMES.find(
      (entry) => entry.single === formState.configuration?.itemName
    ) ?? ITEM_NAMES[0];

  const projectName =
    PROJECT_NAMES.find(
      (entry) => entry.single === formState.configuration?.projectName
    ) ?? PROJECT_NAMES[0];

  const getNextUserFlag = (currentFlag?: Profile["flag"], value?: string) => {
    if (value === "__invite__") {
      return "create";
    }

    if (currentFlag === "add" || currentFlag === "create") {
      return currentFlag;
    }

    return "update";
  };

  const getUserFieldError = (index: number, fieldPath: string) => {
    return (
      formState.validationErrors?.[`toolUsers.${index}.${fieldPath}`] ??
      formState.validationErrors?.[`users.${index}.${fieldPath}`]
    );
  };

  const hasUserRowError = (index: number) => {
    return Object.keys(formState?.validationErrors ?? {}).some(
      (item) =>
        item.startsWith(`toolUsers.${index}.`) ||
        item.startsWith(`users.${index}.`)
    );
  };

  const clearUserFieldErrors = (index: number, fieldPaths: string[]) => {
    const newErrors = {
      ...(formState.validationErrors ?? {}),
    } as Record<string, ExtendedFieldError>;

    fieldPaths.forEach((fieldPath) => {
      delete newErrors[`toolUsers.${index}.${fieldPath}`];
      delete newErrors[`users.${index}.${fieldPath}`];
    });

    setValue("validationErrors", newErrors);
  };

  const getUserIndexById = (userId: string) => {
    return formState.toolUsers?.findIndex((user) => user.id === userId) ?? -1;
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "manageUsers.title") || "Manage Access"}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          mt: 2,
        }}
      >
        <Typography
          sx={{
            color: "#000",
            fontSize: "16px",
            fontWeight: 500,
            width: "100%",
            margin: "auto",
          }}
        >
          {getLocalizedMessage(language, "manageUsers.usersList") ||
            "Users List"}
        </Typography>
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
          }}
        >
          <Grid2
            container
            alignItems="center"
            sx={{
              py: 2,
              paddingLeft: 2,
              borderBottom: "1px solid",
              borderColor: grey[300],
            }}
          >
            <Grid2 size={0.5}></Grid2>
            <Grid2 size={8.5} sx={{ fontSize: "14px", fontWeight: 500 }}>
              {getLocalizedMessage(language, "manageUsers.user") || "User"}
            </Grid2>
            <Grid2
              size={3}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                px: 2,
              }}
            >
              {Boolean(currentSelectedItem?.length) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleDelete(currentSelectedItem ?? []);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  }}
                >
                  Delete Selected
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleAppend}
                startIcon={<Add />}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "7px 16px",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
              >
                Add User
              </Button>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              overflowY: "auto",
              width: "100%",
            }}
          >
            {formState?.toolUsers
              ?.filter((user) => user.flag !== "delete")
              ?.map((field) => {
                const index = getUserIndexById(field.id);
                return (
                  <Accordion
                    sx={{ boxShadow: "none" }}
                    key={field.rowKey ?? field.id}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Grid2
                        container
                        alignItems="center"
                        sx={{ width: "100%" }}
                        key={field.id}
                      >
                        <Grid2 size={0.5}>
                          <Checkbox
                            checked={
                              currentSelectedItem?.includes(field.id ?? "") ??
                              false
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setCurrentSelectedItem((prev) =>
                                isChecked
                                  ? [...(prev ?? []), field.id ?? ""]
                                  : (prev ?? []).filter((i) => i !== field.id)
                              );
                            }}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <Check
                                  style={{
                                    fontSize: 18,
                                    color: grey[700],
                                  }}
                                />
                              </span>
                            }
                          />
                        </Grid2>
                        <Grid2 sx={{ fontSize: "14px" }} size={8.5}>
                          {field.email}
                        </Grid2>
                        <Grid2 sx={{ fontSize: "14px" }} size={2}>
                          {field.name ? (
                            <Chip
                              label={"Active"}
                              sx={{
                                color: SUCCESS_COLORS.text,
                                fontWeight: 500,
                                backgroundColor: SUCCESS_COLORS.background,
                              }}
                            ></Chip>
                          ) : (
                            <Chip
                              label={"Invitation Pending"}
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                                backgroundColor: PURPLE_BADGE_BACKGROUND,
                              }}
                            ></Chip>
                          )}
                        </Grid2>

                        <Grid2 sx={{ fontSize: "14px" }} size={0.5}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDelete([field.id ?? 0])}
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </Grid2>
                        {hasUserRowError(index) && (
                          <Grid2
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.error.main,
                            }}
                            size={0.5}
                          >
                            <Error />
                          </Grid2>
                        )}
                      </Grid2>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          flexGrow: 1,
                          border: "1px solid",
                          borderColor: grey[300],
                          p: 2,
                          borderRadius: "12px",
                        }}
                      >
                        <Grid2 container alignItems="center" spacing={2}>
                          <Grid2 size={6}>
                            <FormFieldWrapper label={`Name`}>
                              <ControlledSelectField
                                value={
                                  field.flag === "create"
                                    ? "__invite__"
                                    : (field?.id ?? "")
                                }
                                options={[
                                  {
                                    label: "+ Invite users to Pimo",
                                    value: "__invite__",
                                  },
                                ].concat(
                                  formState.clientUsers
                                    ?.filter(
                                      (user) =>
                                        (user.name || user.email) &&
                                        (!activeUserIds.includes(user.id) ||
                                          user.id === field.id)
                                    )
                                    .map((user) => ({
                                      label: user?.name ?? user?.email ?? "",
                                      value: user?.id ?? "",
                                    })) ?? []
                                )}
                                onChange={(val) => {
                                  setValue(
                                    `toolUsers.${index}.rowKey`,
                                    field.rowKey ??
                                      field.id ??
                                      crypto.randomUUID()
                                  );

                                  if (val === "__invite__") {
                                    setValue(
                                      `toolUsers.${index}.id`,
                                      crypto.randomUUID()
                                    );
                                    setValue(`toolUsers.${index}.name`, "");
                                    setValue(`toolUsers.${index}.email`, "");
                                    setValue(
                                      `toolUsers.${index}.flag`,
                                      "create"
                                    );
                                    setValue(`toolUsers.${index}.permissions`, {
                                      role: undefined,
                                      client:
                                        formState.configuration?.client ?? "",
                                      tool: formState.configuration?.tool ?? "",
                                      product: "pimo-product",
                                      granularPermissions: [],
                                      projectIds: undefined,
                                      accessLevel: undefined,
                                    });
                                    if (formState.enableSaveButton !== true) {
                                      setValue("enableSaveButton", true);
                                    }
                                    setValue("hasUpdatedUsers", true);
                                    return;
                                  }

                                  // Remove any previously deleted entry with the same id to avoid duplicates
                                  const filteredUsers = (
                                    formState.toolUsers ?? []
                                  ).filter(
                                    (u) =>
                                      !(u.id === val && u.flag === "delete")
                                  );
                                  if (
                                    filteredUsers.length !==
                                    (formState.toolUsers ?? []).length
                                  ) {
                                    setValue("toolUsers", filteredUsers);
                                  }

                                  const updatedIndex = filteredUsers.findIndex(
                                    (u) => u.id === field.id
                                  );
                                  const targetIndex =
                                    updatedIndex !== -1 ? updatedIndex : index;

                                  setValue(
                                    `toolUsers.${targetIndex}.email`,
                                    formState.clientUsers?.find(
                                      (u) => u.id === val
                                    )?.email ?? ""
                                  );
                                  setValue(
                                    `toolUsers.${targetIndex}.name`,
                                    formState.clientUsers?.find(
                                      (u) => u.id === val
                                    )?.name ?? ""
                                  );
                                  setValue(`toolUsers.${targetIndex}.id`, val);
                                  setValue(
                                    `toolUsers.${targetIndex}.flag`,
                                    formState.toolUsers?.find(
                                      (u) => u.id === val
                                    )
                                      ? "update"
                                      : "add"
                                  );
                                  clearUserFieldErrors(targetIndex, [
                                    "email",
                                    "permissions.role",
                                  ]);
                                  if (formState.enableSaveButton !== true) {
                                    setValue("enableSaveButton", true);
                                  }
                                  setValue("hasUpdatedUsers", true);
                                }}
                              />
                            </FormFieldWrapper>
                          </Grid2>

                          <Grid2 size={6}>
                            <FormFieldWrapper
                              label={
                                getLocalizedMessage(
                                  language,
                                  "manageUsers.email"
                                ) || "Email"
                              }
                            >
                              <ControlledTextFormField
                                value={field.email ?? ""}
                                disabled={field.flag !== "create"}
                                onChange={(e) => {
                                  setValue(`toolUsers.${index}.email`, e);
                                  setValue(
                                    `toolUsers.${index}.flag`,
                                    getNextUserFlag(field.flag)
                                  );
                                  clearUserFieldErrors(index, ["email"]);
                                  if (formState.enableSaveButton !== true) {
                                    setValue("enableSaveButton", true);
                                  }
                                  setValue("hasUpdatedUsers", true);
                                }}
                                error={
                                  getUserFieldError(index, "email") !==
                                  undefined
                                }
                                helperText={
                                  getUserFieldError(index, "email")?.message
                                }
                              />
                            </FormFieldWrapper>
                          </Grid2>

                          <Grid2 size={6}>
                            <FormFieldWrapper label={`Role`}>
                              <ControlledSelectField
                                options={
                                  [
                                    { name: "admin", label: "Admin" },
                                    { name: "program", label: "Program" },
                                    {
                                      name: "project",
                                      label:
                                        formState.configuration?.projectName ??
                                        "Project",
                                    },
                                  ]?.map((category) => ({
                                    label: category.label,
                                    value: category.name,
                                  })) ?? []
                                }
                                value={field.permissions?.role ?? ""}
                                onChange={(val) => {
                                  setValue(
                                    `toolUsers.${index}.permissions.role`,
                                    val
                                  );
                                  if (val === "admin") {
                                    setValue(
                                      `toolUsers.${index}.permissions.accessLevel`,
                                      undefined
                                    );
                                    setValue(
                                      `toolUsers.${index}.permissions.projectIds`,
                                      undefined
                                    );
                                  } else if (val === "program") {
                                    setValue(
                                      `toolUsers.${index}.permissions.projectIds`,
                                      undefined
                                    );
                                  }
                                  setValue(
                                    `toolUsers.${index}.flag`,
                                    getNextUserFlag(field.flag)
                                  );
                                  clearUserFieldErrors(index, [
                                    "permissions.role",
                                  ]);
                                  if (formState.enableSaveButton !== true) {
                                    setValue("enableSaveButton", true);
                                  }

                                  setValue("hasUpdatedUsers", true);
                                }}
                                error={
                                  getUserFieldError(
                                    index,
                                    "permissions.role"
                                  ) !== undefined
                                }
                                helperText={
                                  getUserFieldError(index, "permissions.role")
                                    ?.message
                                }
                              />
                            </FormFieldWrapper>
                          </Grid2>
                          {field.permissions?.role !== "admin" && (
                            <Grid2 size={6}>
                              <FormFieldWrapper label={`Access`}>
                                <ControlledSelectField
                                  value={
                                    generateAccessString(
                                      field.permissions?.granularPermissions ??
                                        []
                                    ) ?? ""
                                  }
                                  options={
                                    getAccessOptions(
                                      field.permissions?.role ?? "",
                                      projectName.plural,
                                      itemName.plural
                                    ).map((category) => ({
                                      label: category.label,
                                      value: category.name,
                                    })) ?? []
                                  }
                                  onChange={(val) => {
                                    handleAccessChange({
                                      setValue,
                                      index,
                                      val: Array.isArray(val)
                                        ? (val[0] ?? "")
                                        : val,
                                      formState,
                                      currentFlag: field.flag,
                                    });
                                    if (formState.enableSaveButton !== true) {
                                      setValue("enableSaveButton", true);
                                    }
                                    setValue("hasUpdatedUsers", true);
                                  }}
                                />
                              </FormFieldWrapper>
                            </Grid2>
                          )}
                          {field.permissions?.role === "project" && (
                            <Grid2 size={6}>
                              <FormFieldWrapper
                                label={`${projectName.single}`}
                                uiOptions={{ narrowInfoSlotIcon: true }}
                              >
                                <ControlledSelectField
                                  value={field.permissions?.projectIds ?? []}
                                  multiple={true}
                                  options={
                                    formState.projects
                                      ?.filter((project) => project.name)
                                      .map((project) => ({
                                        label: project?.name ?? "",
                                        value: project?.id ?? "",
                                      })) ?? []
                                  }
                                  onChange={(value) => {
                                    handleProjectIdsChange({
                                      setValue,
                                      index,
                                      value,
                                      formState,
                                      currentFlag: field.flag,
                                    });
                                    if (formState.enableSaveButton !== true) {
                                      setValue("enableSaveButton", true);
                                    }

                                    setValue("hasUpdatedUsers", true);
                                  }}
                                />
                              </FormFieldWrapper>
                            </Grid2>
                          )}
                        </Grid2>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            {(formState?.basicItems?.length ?? 0) > 6 && (
              <Box sx={{ height: 40 }} id={"dummy-to-scroll-into"}></Box>
            )}
          </Box>
        </Paper>
      </Box>
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete}>
        <DialogTitle>
          {getLocalizedMessage(language, "manageUsers.confirmDelete") ||
            "Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {getLocalizedMessage(
              language,
              "manageUsers.confirmDeleteMessage"
            ) || `Are you sure you want to delete this user?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>
            {getLocalizedMessage(language, "manageUsers.cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
